import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ClientService from '../../services/ClientService';
import ClientFormDialog from './ClientFormDialog';

const NEW_OPTION = '__new__';

/** Searchable client select with an inline "create new client" option. */
export default function ClientPicker({ value, onChange, error, helperText }) {
    const { t } = useTranslation();
    const [input, setInput] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    // The typed text at the moment "create" was chosen; the input itself is cleared on blur.
    const [pendingName, setPendingName] = useState('');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const handle = setTimeout(() => {
            ClientService.list({ q: input || undefined, size: 20 })
                .then(res => { if (alive) setOptions(res.items); })
                .catch(() => {})
                .finally(() => alive && setLoading(false));
        }, 220);
        return () => { alive = false; clearTimeout(handle); };
    }, [input]);

    const allOptions = useMemo(() => {
        const list = value && !options.some(o => o.id === value.id) ? [value, ...options] : options;
        return [...list, { id: NEW_OPTION, name: input }];
    }, [options, value, input]);

    return (
        <>
            <Autocomplete
                value={value}
                options={allOptions}
                filterOptions={(x) => x}
                loading={loading}
                getOptionLabel={(o) => (o?.id === NEW_OPTION ? '' : o?.name || '')}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
                onChange={(_, v) => {
                    if (v?.id === NEW_OPTION) { setPendingName(input); setCreateOpen(true); return; }
                    onChange(v);
                }}
                renderOption={(props, o) => {
                    const { key, ...rest } = props;
                    if (o.id === NEW_OPTION) {
                        return (
                            <Box component="li" key="__new" {...rest} sx={{ color: '#427898', fontWeight: 600, gap: 1, borderTop: '1px solid #F1F5F9' }}>
                                <AddIcon fontSize="small" />
                                {input ? t('clients.createNamed', { name: input }) : t('clients.new')}
                            </Box>
                        );
                    }
                    return (
                        <Box component="li" key={o.id} {...rest}>
                            <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{o.name}</Typography>
                                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{[o.phone, o.email, o.city].filter(Boolean).join(' · ') || ' '}</Typography>
                            </Box>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField {...params} size="small" label={t('jobs.fields.client')} required error={error} helperText={helperText}
                               slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) } }} />
                )}
            />
            <ClientFormDialog
                open={createOpen}
                initialName={pendingName}
                onClose={() => setCreateOpen(false)}
                onSaved={(c) => { setCreateOpen(false); onChange(c); }}
            />
        </>
    );
}
